import { useEffect, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from "react";

const API = import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? "/api" : "https://sethlife-prints-api.seth-81a.workers.dev");
const categories = [
  ["business_cards", "Business cards", "Cards that feel considered, not generic."],
  ["postcards", "Postcards", "Mailers, invitations, and handouts."],
  ["flyers", "Flyers", "Clear information with the right paper and finish."],
  ["yard_signs", "Yard signs", "Weather-ready signs with stakes included."],
] as const;

type Category = (typeof categories)[number][0];
interface Quote {
  id: string;
  product: { name: string; quantity: number; configuration: Record<string, string> };
  price: { productCents: number; shippingCents: number; estimatedTaxCents: number; estimatedTotalCents: number };
  destinationZip: string;
  expiresAt: string;
  status: string;
  testData: boolean;
}
interface Message { role: "henry" | "customer"; text: string; suggestedActions?: readonly string[] }
interface ArtworkDimensions { widthInches: number; heightInches: number }
interface ShippingAddress {
  firstName: string; lastName: string; company: string; line1: string; line2: string;
  city: string; state: string; postalCode: string; phone: string;
}

const approvalStatement = "I have reviewed the artwork, spelling, names, phone numbers, addresses, dates, orientation, page order, layout, and print specifications, and I approve this exact proof for production.";

function money(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function inferCategory(description: string): Category {
  const text = description.toLowerCase();
  if (/postcard|mailer|invitation/.test(text)) return "postcards";
  if (/flyer|handout|sheet|menu|brochure/.test(text)) return "flyers";
  if (/yard sign|yard\s*sign|18\s*x\s*24|coroplast/.test(text)) return "yard_signs";
  return "business_cards";
}

async function looksLikeBusinessCard(file: File): Promise<boolean> {
  const nameLooksLikeCard = /business[\s_-]*card|\bbc[\s_-]*\d|card[\s_-]*(front|back|final)/i.test(file.name);
  if (nameLooksLikeCard) return true;

  try {
    if (file.type === "application/pdf") {
      const bytes = await file.slice(0, 2_000_000).arrayBuffer();
      const source = new TextDecoder("latin1").decode(bytes);
      const box = /\/MediaBox\s*\[\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)/m.exec(source);
      if (box) {
        const widthInches = Math.abs(Number(box[3]) - Number(box[1])) / 72;
        const heightInches = Math.abs(Number(box[4]) - Number(box[2])) / 72;
        const shortSide = Math.min(widthInches, heightInches);
        const longSide = Math.max(widthInches, heightInches);
        const ratio = longSide / shortSide;
        return shortSide >= 1.5 && shortSide <= 4.5 && longSide >= 2.5 && longSide <= 6 && ratio >= 1.55 && ratio <= 1.95;
      }
    }
    if (file.type.startsWith("image/") && typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file);
      const ratio = Math.max(bitmap.width, bitmap.height) / Math.min(bitmap.width, bitmap.height);
      bitmap.close();
      return ratio >= 1.55 && ratio <= 1.95;
    }
  } catch {
    // A file can still be queued even when browser-side inspection is unavailable.
  }
  return false;
}

async function artworkDimensions(file: File): Promise<ArtworkDimensions | null> {
  try {
    if (file.type === "application/pdf") {
      const source = new TextDecoder("latin1").decode(await file.slice(0, 2_000_000).arrayBuffer());
      const box = /\/(?:MediaBox|TrimBox)\s*\[\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)/m.exec(source);
      if (box) return { widthInches: Math.abs(Number(box[3]) - Number(box[1])) / 72, heightInches: Math.abs(Number(box[4]) - Number(box[2])) / 72 };
    }
    if (file.type.startsWith("image/") && typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file);
      const result = { widthInches: bitmap.width / 300, heightInches: bitmap.height / 300 };
      bitmap.close();
      return result;
    }
  } catch { return null; }
  return null;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API}${path}`, { credentials: "include", ...init, headers: { "content-type": "application/json", ...init?.headers } });
  const payload = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? "Request failed");
  return payload;
}

export default function App() {
  const [conversationId] = useState(() => crypto.randomUUID());
  const [printNeed, setPrintNeed] = useState("");
  const [zip, setZip] = useState("76903");
  const [message, setMessage] = useState("");
  const [artworkFiles, setArtworkFiles] = useState<File[]>([]);
  const [messages, setMessages] = useState<Message[]>([
    { role: "henry", text: "What should we make? You can describe it or upload the artwork.", suggestedActions: ["Get a quote", "Place an order", "Browse options"] },
  ]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [handoffRequested, setHandoffRequested] = useState(false);
  const [proofApproved, setProofApproved] = useState(false);
  const [jobName, setJobName] = useState("");
  const [customerPo, setCustomerPo] = useState("");
  const [orderState, setOrderState] = useState<string | null>(null);
  const [shipping, setShipping] = useState<ShippingAddress>({ firstName: "", lastName: "", company: "", line1: "", line2: "", city: "", state: "", postalCode: "76903", phone: "" });

  useEffect(() => {
    void api<{ user: { email: string } }>("/auth/me")
      .then(({ user }) => {
        setSignedIn(true); setEmail(user.email);
        if (user.email.toLowerCase() === "seth@sethlife.com") setShipping({ firstName: "Seth", lastName: "Chomout", company: "SethLife", line1: "12 E Twohig Ave", line2: "Suite 200-A", city: "San Angelo", state: "Texas", postalCode: "76903", phone: "" });
      })
      .catch(() => undefined);
  }, []);

  async function sendText(text: string) {
    if (!text || busy) return;
    setMessages((current) => [...current, { role: "customer", text }]);
    setMessage(""); setBusy(true); setError(null);
    try {
      const response = await api<{ message: string; suggestedActions?: string[] }>("/henry/respond", {
        method: "POST",
        body: JSON.stringify({ conversationId, message: text, history: messages }),
      });
      setMessages((current) => [...current, { role: "henry", text: response.message, suggestedActions: response.suggestedActions ?? [] }]);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Henry is unavailable."); }
    finally { setBusy(false); }
  }

  function sendMessage(event: FormEvent | KeyboardEvent<HTMLTextAreaElement>) {
    event.preventDefault();
    void sendText(message.trim());
  }

  async function calculate(): Promise<Quote | null> {
    setBusy(true); setError(null);
    try {
      const result = await api<Quote>("/quotes/calculate", {
        method: "POST",
        body: JSON.stringify({ category: inferCategory(printNeed), destinationZip: zip, pricingTier: new URLSearchParams(location.search).get("tier") === "client" ? "client" : "public" }),
      });
      setQuote(result);
      setMessages((current) => [...current, { role: "henry", text: `I put together a ${result.product.name.toLowerCase()} estimate. It includes standard shipping to ${zip} and estimated tax.`, suggestedActions: ["Save this job", "Change quantity", "Add artwork"] }]);
      return result;
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The quote could not be calculated."); }
    finally { setBusy(false); }
    return null;
  }

  async function prepareOrder() {
    const result = await calculate();
    if (!result) return;
    setHandoffRequested(true);
    setMessages((current) => [...current, { role: "henry", text: "I’ve got the starting details. Save this job and I’ll route the artwork, proof, and final production review to Seth." }]);
    document.getElementById("save")?.scrollIntoView({ behavior: "smooth" });
  }

  async function addArtworkFiles(event: ChangeEvent<HTMLInputElement>) {
    const incoming = Array.from(event.target.files ?? []);
    if (!incoming.length) return;
    event.currentTarget.value = "";
    setArtworkFiles((current) => {
      const existing = new Set(current.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      return [...current, ...incoming.filter((file) => !existing.has(`${file.name}:${file.size}:${file.lastModified}`))];
    });
    const recognizedFile = (await Promise.all(incoming.map(async (file) => ({ file, isCard: await looksLikeBusinessCard(file) })))).find((item) => item.isCard)?.file;
    if (recognizedFile) {
      setPrintNeed((current) => current.trim() || `Business card artwork ready: ${recognizedFile.name}`);
      setMessages((current) => [...current, { role: "henry", text: `I recognize ${recognizedFile.name} as business-card artwork. How many do you need?`, suggestedActions: ["500", "1,000", "Custom quantity"] }]);
    }
  }

  async function submitFastOrder(event: FormEvent) {
    event.preventDefault();
    if (!quote || artworkFiles.length !== 2 || !proofApproved) return;
    setBusy(true); setError(null); setOrderState(null);
    try {
      const dimensions = await artworkDimensions(artworkFiles[0]!);
      if (!dimensions) throw new Error("I couldn’t verify the first file’s finished dimensions. Please use a PDF, PNG, or JPG with a standard business-card page size.");
      const horizontal = dimensions.widthInches > dimensions.heightInches;
      const assetIds: string[] = [];
      for (const file of artworkFiles) {
        const grant = await api<{ assetId: string; uploadUrl: string; requiredHeaders: Record<string, string> }>("/uploads/grant", {
          method: "POST", body: JSON.stringify({ filename: file.name, contentType: file.type, sizeBytes: file.size }),
        });
        const upload = await fetch(grant.uploadUrl, { method: "PUT", headers: grant.requiredHeaders, body: file });
        if (!upload.ok) throw new Error(`The secure upload failed for ${file.name}.`);
        await api(`/uploads/${grant.assetId}/complete`, { method: "POST", body: "{}" });
        assetIds.push(grant.assetId);
      }
      const proof = await api<{ id: string }>("/proofs", {
        method: "POST",
        body: JSON.stringify({
          quoteId: quote.id, artworkAssetIds: assetIds, warnings: [],
          specification: {
            orientation: horizontal ? "horizontal" : "vertical", fitMode: "FIT",
            finishedWidthInches: horizontal ? 3.5 : 2, finishedHeightInches: horizontal ? 2 : 3.5,
            requiredBleedInches: 0.125, artworkWidthInches: dimensions.widthInches, artworkHeightInches: dimensions.heightInches,
          },
        }),
      });
      await api(`/proofs/${proof.id}/approve`, { method: "POST", body: JSON.stringify({ statement: approvalStatement, acknowledgedWarnings: [] }) });
      const order = await api<{ orderNumber: string; state: string }>("/orders", {
        method: "POST",
        body: JSON.stringify({
          proofId: proof.id, jobName: jobName || printNeed || "Business cards", customerPo: customerPo || undefined,
          shippingAddress: { ...shipping, countryCode: "US", company: shipping.company || undefined, line2: shipping.line2 || undefined, phone: shipping.phone || undefined },
        }),
      });
      setOrderState(`${order.orderNumber} is queued. Henry will email Seth only after the Printograph cart passes every checkout check.`);
      setMessages((current) => [...current, { role: "henry", text: `${order.orderNumber} is queued. I’m preparing the Printograph cart now; Seth will only get the email when it is ready for CVV and final placement.` }]);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The order could not be queued."); }
    finally { setBusy(false); }
  }

  function updateShipping(field: keyof ShippingAddress, value: string) {
    setShipping((current) => ({ ...current, [field]: value }));
  }

  function chooseStartingPath(path: "quote" | "order" | "browse") {
    const text = path === "quote" ? "I need a quote" : path === "order" ? "I want to place an order" : "Show me print options";
    void sendText(text);
    document.getElementById("message")?.focus();
  }

  async function requestMagicLink(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      await api("/auth/magic-link", { method: "POST", body: JSON.stringify({ email, returnTo: quote ? "/?signedIn=1#checkout" : "/?signedIn=1#save" }) });
      setMagicLinkSent(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The sign-in link could not be sent."); }
    finally { setBusy(false); }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="SethLife Prints home"><img src="/brand/logos/sethlife-prints-primary-horizontal-full-color.svg" alt="SethLife Prints" /></a>
        <div className="private-badge"><span /> Early access</div>
        <button className="quiet-button" onClick={() => document.getElementById("save")?.scrollIntoView({ behavior: "smooth" })}>{signedIn ? email : "Save my work"}</button>
      </header>

      <main id="top">
        <section className="hero">
          <p className="eyebrow">A little more attention.</p>
          <h1>Tell Henry what you need printed.</h1>
          <p className="lede">A print shop that starts with what you are trying to accomplish. Get a practical recommendation, a clear all-in estimate, and a real path from artwork to delivery.</p>
          <div className="promise-row" aria-label="SethLife Prints service promises">
            <span>All-in estimates</span><span>Proof before production</span><span>Human review when it matters</span>
          </div>
        </section>

        <section className="workspace" aria-label="Print quote workspace">
          <div className="conversation-panel">
            <div className="conversation-heading"><div><p className="eyebrow">Henry</p><h2>Your print concierge</h2></div><div className="attention"><span /> Paying attention</div></div>
            <div className="messages" aria-live="polite">
              {messages.map((item, index) => <div className={`message ${item.role}`} key={`${item.role}-${index}`}><span className="avatar">{item.role === "henry" ? <img src="/brand/logos/sethlife-prints-icon-full-color.svg" alt="Henry" /> : "You"}</span><div className="message-content"><p>{item.text}</p>{item.role === "henry" && item.suggestedActions && item.suggestedActions.length > 0 && <div className="suggested-actions">{item.suggestedActions.slice(0, 3).map((action) => <button type="button" key={action} onClick={() => void sendText(action)} disabled={busy}>{action}</button>)}</div>}</div></div>)}
              {busy && <div className="message henry"><span className="avatar"><img src="/brand/logos/sethlife-prints-icon-full-color.svg" alt="Henry" /></span><p className="thinking">Working through that…</p></div>}
            </div>
            <form className="composer" onSubmit={sendMessage}>
              <label htmlFor="message">Tell Henry what you’re making</label>
              <div><textarea id="message" value={message} onChange={(event) => setMessage(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void sendText(message.trim()); } }} placeholder="For example: I need cards for a new employee before next Friday…" rows={2} /><button disabled={busy || !message.trim()} aria-label="Send message">↑</button></div>
              <div className="composer-actions"><label className="upload-button" htmlFor="artwork-upload"><input id="artwork-upload" type="file" accept="application/pdf,image/jpeg,image/png,image/tiff,image/svg+xml" multiple onChange={(event) => void addArtworkFiles(event)} />↑ Upload artwork</label><small>PDF, JPG, PNG, TIFF, or SVG</small></div>
              {artworkFiles.length > 0 && <div className="file-list" aria-live="polite">{artworkFiles.map((file) => <div className="file-chip" key={`${file.name}:${file.size}:${file.lastModified}`}><span>{file.name}</span><button type="button" aria-label={`Remove ${file.name}`} onClick={() => setArtworkFiles((current) => current.filter((item) => item !== file))}>×</button></div>)}<small>{signedIn ? "Artwork is ready to save with this job." : "Artwork is staged here. Save the job to send it securely."}</small></div>}
              <small className="composer-hint">Press Enter to send · Shift + Enter for a new line</small>
            </form>
          </div>

          <aside className="spec-panel">
            <div className="step-label">01 · Start with the job</div>
            <h2>What do you need printed?</h2>
            <div className="top-intents" aria-label="Starting paths"><button type="button" onClick={() => chooseStartingPath("quote")} disabled={busy}><strong>Get a quote</strong><small>See the all-in number.</small></button><button type="button" onClick={() => chooseStartingPath("order")} disabled={busy}><strong>Place an order</strong><small>Upload and queue it.</small></button><button type="button" onClick={() => chooseStartingPath("browse")} disabled={busy}><strong>Browse options</strong><small>Compare what fits.</small></button></div>
            <label className="field" htmlFor="print-need"><span>Or tell Henry in your own words</span><textarea id="print-need" rows={3} value={printNeed} onChange={(event) => { setPrintNeed(event.target.value); setQuote(null); setHandoffRequested(false); }} placeholder="500 business cards for a new employee…" /></label>
            <p className="field-note">Upload artwork in the chat, or describe what you’re trying to make. Henry will infer the product and ask only for what’s missing.</p>
            <details className="delivery-details"><summary>Delivery details</summary><label className="field" htmlFor="zip"><span>Delivery ZIP</span><input id="zip" inputMode="numeric" maxLength={10} value={zip} onChange={(event) => setZip(event.target.value)} /></label></details>
            <div className="action-stack"><button className="primary-button" disabled={busy || !printNeed.trim() || !/^\d{5}(?:-\d{4})?$/.test(zip)} onClick={() => void prepareOrder()}>Prepare my order</button><button className="secondary-button" disabled={busy || !printNeed.trim() || !/^\d{5}(?:-\d{4})?$/.test(zip)} onClick={() => void calculate()}>Just show me the estimate</button></div>
          </aside>
        </section>

        {error && <div className="error" role="alert">{error}</div>}

        {quote && <section className="quote-card" id="checkout" aria-label="Quote estimate">
          <div className="quote-title"><div><p className="eyebrow">Early-access estimate · {quote.status.replaceAll("_", " ")}</p><h2>{quote.product.name}</h2><p>{Object.values(quote.product.configuration).join(" · ")}</p></div><div className="quote-total"><span>Estimated total</span><strong>{money(quote.price.estimatedTotalCents)}</strong><small>to {quote.destinationZip}</small></div></div>
          <div className="price-lines"><div><span>Printing</span><strong>{money(quote.price.productCents)}</strong></div><div><span>Standard shipping</span><strong>{money(quote.price.shippingCents)}</strong></div><div><span>Estimated tax</span><strong>{money(quote.price.estimatedTaxCents)}</strong></div></div>
          <div className="assumptions"><strong>No checkout surprise.</strong> Final tax and shipping are confirmed after a complete delivery address. Early-access orders may include an assisted supplier step; Henry will explain any review needed before production.</div>
          <div className="quote-footer"><span>Estimate expires {new Date(quote.expiresAt).toLocaleDateString()}</span><button className="primary-button" onClick={() => document.getElementById("save")?.scrollIntoView({ behavior: "smooth" })}>Save and continue</button></div>
        </section>}

        {handoffRequested && quote && !signedIn && <section className="handoff-card" aria-live="polite"><div><p className="eyebrow">Ready for the next step</p><h2>Save it, then Henry takes over.</h2><p>Use your magic link below. When you return, upload the front and back, approve the proof statement, and queue the order.</p></div><button className="primary-button" onClick={() => document.getElementById("save")?.scrollIntoView({ behavior: "smooth" })}>Save this order</button></section>}

        {handoffRequested && quote && signedIn && <section className="fast-order" aria-label="Fast order handoff">
          <div><p className="eyebrow">Direct-to-cart handoff</p><h2>Queue this order for Seth’s cart.</h2><p>Upload exactly two files in the chat—front first, back second. Henry applies <strong>FIT</strong>, checks both sides and the bleed view, selects standard shipping and blind shipping, then emails Seth a prepared checkout link.</p></div>
          <form onSubmit={submitFastOrder}>
            <div className="field-grid"><label className="field"><span>Job name</span><input required value={jobName} onChange={(event) => setJobName(event.target.value)} placeholder="Claire’s business cards" /></label><label className="field"><span>PO / customer reference</span><input value={customerPo} onChange={(event) => setCustomerPo(event.target.value)} placeholder="Claire’s cards" /></label></div>
            <div className="field-grid"><label className="field"><span>First name</span><input required value={shipping.firstName} onChange={(event) => updateShipping("firstName", event.target.value)} /></label><label className="field"><span>Last name</span><input required value={shipping.lastName} onChange={(event) => updateShipping("lastName", event.target.value)} /></label></div>
            <label className="field"><span>Company</span><input value={shipping.company} onChange={(event) => updateShipping("company", event.target.value)} /></label>
            <label className="field"><span>Address</span><input required value={shipping.line1} onChange={(event) => updateShipping("line1", event.target.value)} /></label>
            <label className="field"><span>Suite / unit</span><input value={shipping.line2} onChange={(event) => updateShipping("line2", event.target.value)} /></label>
            <div className="field-grid three"><label className="field"><span>City</span><input required value={shipping.city} onChange={(event) => updateShipping("city", event.target.value)} /></label><label className="field"><span>State</span><input required value={shipping.state} onChange={(event) => updateShipping("state", event.target.value)} /></label><label className="field"><span>ZIP</span><input required value={shipping.postalCode} onChange={(event) => updateShipping("postalCode", event.target.value)} pattern="\d{5}(-\d{4})?" /></label></div>
            <label className="approval-check"><input type="checkbox" checked={proofApproved} onChange={(event) => setProofApproved(event.target.checked)} /><span>{approvalStatement}</span></label>
            <div className="order-readiness"><span className={artworkFiles.length === 2 ? "ready" : ""}>{artworkFiles.length}/2 artwork files</span><span className={proofApproved ? "ready" : ""}>Proof approval</span><span className={shipping.line1 && shipping.city && shipping.state ? "ready" : ""}>Shipping details</span></div>
            <button className="primary-button" disabled={busy || artworkFiles.length !== 2 || !proofApproved}>Prepare Seth’s Printograph cart</button>
            {orderState && <p className="success" role="status">{orderState}</p>}
          </form>
        </section>}

        <section className="save-panel" id="save">
          <div><p className="eyebrow">Keep the thread</p><h2>{signedIn ? "Your work is connected." : "Save this job without a password."}</h2><p>{signedIn ? "Artwork intake, proof approval, secure checkout, and order updates stay connected to this job." : "We’ll email a secure, single-use link. No password to remember."}</p></div>
          {!signedIn && <form onSubmit={requestMagicLink}><label className="field" htmlFor="email"><span>Email address</span><input id="email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" /></label><button className="primary-button" disabled={busy}>{magicLinkSent ? "Link requested" : "Email my secure link"}</button>{magicLinkSent && <small>Check your inbox. The link expires in 15 minutes.</small>}</form>}
        </section>

        <section className="trust-row"><div><strong>Deterministic pricing</strong><span>Henry never invents a price.</span></div><div><strong>Proof before production</strong><span>Every order keeps an approval record.</span></div><div><strong>Human judgment</strong><span>Seth reviews new artwork and exceptions.</span></div></section>
      </main>
      <footer><span>SethLife Prints · Early access</span><span>Henry is SethLife Prints’ digital print concierge.</span><nav className="legal-links" aria-label="Legal and support"><a href="/terms.html">Terms</a><a href="/privacy.html">Privacy</a><a href="/shipping.html">Shipping</a><a href="/returns.html">Returns</a><a href="mailto:henry@print.sethlife.com">Contact</a></nav></footer>
    </div>
  );
}
